import { useState, useEffect, useRef, useCallback } from 'react';

const ICON_SIZE = 22;

const useIconOverlay = (containerRef, annMeta, hiddenGroups) => {
  const [icons, setIcons] = useState([]);
  const annMetaRef = useRef(annMeta);
  const hiddenRef  = useRef(hiddenGroups);
  const iconsRef   = useRef([]);

  useEffect(() => { annMetaRef.current = annMeta; },     [annMeta]);
  useEffect(() => { hiddenRef.current  = hiddenGroups; }, [hiddenGroups]);

  const getPositions = useCallback(() => {
    const gd = containerRef.current?.querySelector('.js-plotly-plot');
    if (!gd?._fullLayout) return null;
    const { xaxis, yaxis2, margin } = gd._fullLayout;
    if (!xaxis?.d2p || !yaxis2?.l2p) return null;

    return annMetaRef.current
      .filter((ev) => ev.hasSvg && !hiddenRef.current.has(ev.legendGroup))
      .map((ev) => ({
        xPx:      margin.l + xaxis.d2p(ev.xDate),
        yPx:      margin.t + yaxis2.l2p(ev.markerY),
        color:    ev.style.color,
        iconType: ev.iconType,
        key:      `${ev.legendGroup}-${ev.t}-${ev.itemIdx}`,
      }));
  }, [containerRef]);

  // Direct DOM update — bypasses React reconciliation, zero lag.
  const updateDom = useCallback((result) => {
    const overlay = containerRef.current?.querySelector('.leg-icon-overlay');
    if (!overlay) return;
    result.forEach((ic) => {
      const group = overlay.querySelector(`[data-key="${CSS.escape(ic.key)}"]`);
      if (group) {
        group.setAttribute(
          'transform',
          `translate(${ic.xPx - ICON_SIZE / 2}, ${ic.yPx - ICON_SIZE / 2})`
        );
      }
    });
  }, [containerRef]);

  // Full compute: detects set changes (new/removed icons) → React state,
  // or position-only changes → direct DOM.
  const compute = useCallback(() => {
    const result = getPositions();
    if (!result) return;

    const prev = iconsRef.current;
    const setChanged =
      result.length !== prev.length ||
      result.some(
        (ic, i) =>
          ic.key      !== prev[i]?.key ||
          ic.iconType !== prev[i]?.iconType ||
          ic.color    !== prev[i]?.color
      );

    if (setChanged) {
      iconsRef.current = result;
      setIcons(result);
    } else {
      updateDom(result);
    }
  }, [getPositions, updateDom]);

  // Position-only update used inside the rAF drag loop (no set-change check).
  const computePositionsOnly = useCallback(() => {
    const result = getPositions();
    if (result) updateDom(result);
  }, [getPositions, updateDom]);

  // Initial positioning + data / visibility changes.
  useEffect(() => {
    const raf = requestAnimationFrame(compute);
    return () => cancelAnimationFrame(raf);
  }, [annMeta, hiddenGroups, compute]);

  // Plotly events + rAF drag loop.
  //
  // Strategy:
  //   - plotly_relayout  → full compute (zoom, resize, drag-end).
  //   - mousedown on .drag → start rAF loop for the duration of the drag.
  //     rAF fires BEFORE browser paint, in the same frame Plotly updated d2p,
  //     so icons stay perfectly in sync with no perceived lag.
  //   - mouseup → stop loop + one final update.
  useEffect(() => {
    if (!containerRef.current) return;

    let gd       = null;
    let rafId    = null;
    let dragging = false;

    const tick = () => {
      computePositionsOnly();
      if (dragging) rafId = requestAnimationFrame(tick);
    };

    const onMouseDown = (e) => {
      if (!e.target.closest?.('.drag')) return;
      dragging = true;
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(tick);
    };

    const stopDrag = () => {
      if (!dragging) return;
      dragging = false;
      cancelAnimationFrame(rafId);
      // One final rAF after Plotly settles at the released position.
      rafId = requestAnimationFrame(computePositionsOnly);
    };

    const attach = (el) => {
      gd = el;
      gd.on('plotly_relayout', compute);
      // plotly_afterplot fires after every Plotly.react() / Plotly.newPlot() call,
      // ensuring icons reposition when traces or layout change without a relayout event.
      gd.on('plotly_afterplot', compute);
      gd.addEventListener('mousedown', onMouseDown);
      document.addEventListener('mouseup', stopDrag);
      // Guard against drag never ending when mouse is released outside the browser window.
      window.addEventListener('blur', stopDrag);
    };

    const detach = () => {
      gd?.removeListener?.('plotly_relayout', compute);
      gd?.removeListener?.('plotly_afterplot', compute);
      gd?.removeEventListener?.('mousedown', onMouseDown);
      document.removeEventListener('mouseup', stopDrag);
      window.removeEventListener('blur', stopDrag);
      cancelAnimationFrame(rafId);
    };

    const existing = containerRef.current.querySelector('.js-plotly-plot');
    if (existing) {
      attach(existing);
      return detach;
    }

    const observer = new MutationObserver(() => {
      const el = containerRef.current?.querySelector('.js-plotly-plot');
      if (el && !gd) { attach(el); observer.disconnect(); }
    });
    observer.observe(containerRef.current, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      detach();
    };
  }, [containerRef, compute, computePositionsOnly]);

  return icons;
};

export default useIconOverlay;
